import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { paypalGetBaid, paypalInit } from '../../sagas/sagaActions';
import { selectPayPalHidden } from '../../store/appSelectors';
import { setPlacing } from '../../store/checkoutSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectCheckoutBlocked } from '../../store/noticeSelectors';
import {
  setPaymentError,
  setPaymentMethod,
  setSelectedMethod,
} from '../../store/paymentSlice';
import type { SelectedMethod } from '../../types/payment';
import { isUSA } from '../../utils/validators/countries';
import { isValid } from '../../utils/validators/validators';
import Zuora, { type ZuoraResponse } from '../../utils/zuora';
import { currency } from '../data';
import { ChevronDownIcon, LockIcon } from '../icons';
import { Modal } from '../Modal';
import styles from './Payment.module.scss';
import { purchaseLimits } from './paymentConstants';

function PurchaseLimitModal({
  currencySymbol,
  limit,
  onClose,
}: {
  currencySymbol: string;
  limit: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose}>
      <h3 className={styles.modalTitle}>{t('Purchase Limit Reached')}</h3>
      <p className={styles.modalBody}>
        {t(
          'Purchases over {{currencySymbol}}{{limit}} cannot be completed online.',
          {
            currencySymbol,
            limit,
          },
        )}
      </p>
      <a
        className={styles.modalLink}
        href="https://www.chaos.com/contact-us/sales"
        target="_blank"
        rel="noreferrer"
      >
        {t('Please contact our Sales team to proceed with your order.')}
      </a>
      <div className={styles.modalActions}>
        <button
          type="button"
          className={styles.modalCloseBtn}
          onClick={onClose}
        >
          {t('Close')}
        </button>
      </div>
    </Modal>
  );
}

function ZuoraFrame({
  total,
  submitLabel,
  onPayAttempt,
}: {
  total: number;
  submitLabel?: string;
  onPayAttempt: () => boolean;
}) {
  const dispatch = useAppDispatch();
  const signature = useAppSelector((state) => state.payment.signature);
  const selectedMethod = useAppSelector(
    (state) => state.payment.selectedMethod,
  );
  const placing = useAppSelector((state) => state.checkout.placing);
  const { form, errors: fieldErrors } = useAppSelector(
    (state) => state.billing,
  );
  const { session, localization } = useAppSelector((state) => state.app);
  const blocked = useAppSelector(selectCheckoutBlocked);
  const [iframeReady, setIframeReady] = useState(false);
  const { t } = useTranslation();
  const billingComplete = isValid(form, fieldErrors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-renders the hosted page only when a fresh signature arrives
  useEffect(() => {
    setIframeReady(false);
    if (!signature || !localization) {
      return;
    }

    const successCallback = (response: ZuoraResponse) => {
      if (!response.success) {
        if (response.errorMessage?.includes('refresh')) {
          dispatch(setSelectedMethod(selectedMethod));
        } else {
          dispatch(
            setPaymentError(response.errorMessage || t('Payment failed.')),
          );
        }
        dispatch(setPlacing(false));
        return;
      }
      dispatch(
        setPaymentMethod({
          Type:
            selectedMethod === 'CreditCardUS' ? 'CreditCard' : selectedMethod,
          Id: response.refId ?? '',
        }),
      );
    };

    const errorCallback = (key: string, _code: string, message: string) => {
      dispatch(setPlacing(false));
      Zuora.sendErrorMessageToHpm(key, message);
    };

    const onRemove = () => {
      dispatch(setPlacing(false));
    };

    const pageId =
      selectedMethod === 'CreditCardUS'
        ? window.ZUORA_PAYMENT_PAGE_CREDIT_CARD_US_ID
        : window.ZUORA_PAYMENT_PAGE_CREDIT_CARD_ID;

    Zuora.renderWithErrorHandler(
      {
        signature: signature.signature,
        token: signature.token,
        tenantId: Number(signature.tenantId),
        key: signature.key,
        style: 'inline',
        id: pageId,
        submitEnabled: true,
        url: window.ZUORA_PAYMENT_PAGE,
        locale: 'en',
        authorizationAmount: total,
        field_currency: localization.currencyCode,
      },
      {
        creditCardHolderName: `${form.firstName} ${form.lastName}`,
        firstName: form.firstName,
        lastName: form.lastName,
        email: session?.Email || form.email,
      },
      successCallback,
      errorCallback,
      '100%',
      '100%',
      onRemove,
    );

    const iframe = document.getElementById('z_hppm_iframe');
    const onLoad = () => setIframeReady(true);
    iframe?.addEventListener('load', onLoad);
    return () => iframe?.removeEventListener('load', onLoad);
  }, [signature]);

  const onPay = () => {
    if (!onPayAttempt()) {
      return;
    }
    dispatch(setPlacing(true));
    Zuora.submit();
  };

  return (
    <>
      {signature && <div id="zuora_payment" className={styles.zuoraFrame} />}
      <button
        type="button"
        className={styles.payBtn}
        onClick={onPay}
        disabled={!iframeReady || placing || blocked || !billingComplete}
      >
        <LockIcon className={styles.payIcon} />
        {submitLabel ??
          t('Pay {{currencySymbol}}{{amount}}', {
            currencySymbol: localization?.currencySymbol ?? '',
            amount: currency(total),
          })}
      </button>
    </>
  );
}

function PayPalMethod({ onPayAttempt }: { onPayAttempt: () => boolean }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const placing = useAppSelector((state) => state.checkout.placing);
  const { form, errors: fieldErrors } = useAppSelector(
    (state) => state.billing,
  );
  const blocked = useAppSelector(selectCheckoutBlocked);
  const billingComplete = isValid(form, fieldErrors);

  useEffect(() => {
    const handlePayPalMessage = (e: MessageEvent) => {
      if (e.data?.type !== 'paypalData') {
        return;
      }
      dispatch(paypalGetBaid(e.data.token));
    };

    window.addEventListener('message', handlePayPalMessage);
    return () => window.removeEventListener('message', handlePayPalMessage);
  }, [dispatch]);

  const onPay = () => {
    if (!onPayAttempt()) {
      return;
    }
    dispatch(paypalInit());
  };

  return (
    <button
      type="button"
      className={styles.payBtn}
      onClick={onPay}
      disabled={blocked || placing || !billingComplete}
    >
      {t('Checkout with PayPal')}
    </button>
  );
}

export function Payment({
  total,
  submitLabel,
}: {
  total: number;
  submitLabel?: string;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const localization = useAppSelector((state) => state.app.localization);
  const selectedMethod = useAppSelector(
    (state) => state.payment.selectedMethod,
  );
  const error = useAppSelector((state) => state.payment.error);
  const payPalHidden = useAppSelector(selectPayPalHidden);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const currencyCode = localization?.currencyCode ?? '';
  const cardMethod: SelectedMethod = isUSA(localization?.countryCode ?? '')
    ? 'CreditCardUS'
    : 'CreditCard';

  // biome-ignore lint/correctness/useExhaustiveDependencies: fires once, when localization first resolves
  useEffect(() => {
    if (!localization) {
      return;
    }
    dispatch(setSelectedMethod(cardMethod));
  }, [localization]);

  useEffect(() => {
    if (!payPalHidden || selectedMethod !== 'PayPal') {
      return;
    }
    dispatch(setSelectedMethod(cardMethod));
  }, [payPalHidden, selectedMethod, cardMethod, dispatch]);

  const checkPurchaseLimit = (): boolean => {
    const limit = purchaseLimits[currencyCode];
    if (limit !== undefined && total > limit) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  };

  return (
    <div className={styles.wrap}>
      <h3 className={styles.sectionTitle}>{t('Payment Details')}</h3>
      <label className={styles.formField}>
        <span className={styles.formLabel}>{t('Payment method')}</span>
        <div className={styles.control}>
          <select
            className={`${styles.controlBox} ${styles.selectBox} ${styles.controlBoxIcon} ${styles.nativeSelect}`}
            value={selectedMethod}
            onChange={(e) =>
              dispatch(setSelectedMethod(e.target.value as SelectedMethod))
            }
          >
            <option value="" disabled>
              {t('Please select')}
            </option>
            <option value={cardMethod}>{t('Credit/Debit card')}</option>
            {!payPalHidden && <option value="PayPal">PayPal</option>}
          </select>
          <ChevronDownIcon
            className={`${styles.selectChevron} ${styles.selectChevronOverlay}`}
          />
        </div>
      </label>

      {selectedMethod === 'PayPal' && !payPalHidden ? (
        <PayPalMethod onPayAttempt={checkPurchaseLimit} />
      ) : (
        <ZuoraFrame
          total={total}
          submitLabel={submitLabel}
          onPayAttempt={checkPurchaseLimit}
        />
      )}

      {error && <div className={styles.paymentError}>{error}</div>}

      {showLimitModal && localization && (
        <PurchaseLimitModal
          currencySymbol={localization.currencySymbol}
          limit={purchaseLimits[currencyCode]}
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </div>
  );
}
